import { useForm } from "@inertiajs/react"
import type { FormEventHandler } from "react"

interface CreditData {
  id?: number
  user_id: number | null
  credit_year: number
  hours_requested: number
  reason: string
}

interface Employee {
  id: number
  name: string
}

interface YearOption {
  value: number
  label: string
}

interface Props {
  credit: CreditData
  employees: Employee[]
  yearOptions: YearOption[]
  errors?: Record<string, string[]>
  onCancel: () => void
}

export default function PtoCreditForm({
  credit,
  employees,
  yearOptions,
  errors,
  onCancel,
}: Props) {
  const { data, setData, post, put, processing, transform } = useForm({
    user_id: credit.user_id ?? "",
    credit_year: credit.credit_year,
    hours_requested: credit.hours_requested,
    reason: credit.reason,
  })

  transform((data) => ({ pto_credit: data }))

  const submit: FormEventHandler = (e) => {
    e.preventDefault()
    if (credit.id) {
      put(`/pto_credits/${credit.id}`)
    } else {
      post("/pto_credits")
    }
  }

  const inputClass =
    "mt-1 block w-full rounded-md border-hpi-light-gray shadow-sm focus:border-hpi-active-blue focus:ring-hpi-active-blue"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"
  const errorClass = "mt-1 text-sm text-hpi-red"

  return (
    <form onSubmit={submit} className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <label className={labelClass}>Employee</label>
          <select
            className={inputClass}
            value={data.user_id}
            onChange={(e) =>
              setData("user_id", e.target.value ? parseInt(e.target.value) : "")
            }
            required
            disabled={!!credit.id}
          >
            <option value="">Select an employee...</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.name}
              </option>
            ))}
          </select>
          {errors?.user_id && <p className={errorClass}>{errors.user_id[0]}</p>}
        </div>

        <div>
          <label className={labelClass}>Year</label>
          <select
            className={inputClass}
            value={data.credit_year}
            onChange={(e) => setData("credit_year", parseInt(e.target.value))}
            required
            disabled={!!credit.id}
          >
            {yearOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {errors?.start_date && (
            <p className={errorClass}>{errors.start_date[0]}</p>
          )}
        </div>

        <div>
          <label className={labelClass}>Credit Hours</label>
          <input
            type="number"
            step="0.25"
            min="0.25"
            className={inputClass}
            value={data.hours_requested}
            onChange={(e) =>
              setData("hours_requested", parseFloat(e.target.value) || 0)
            }
            required
          />
          {errors?.hours_requested && (
            <p className={errorClass}>{errors.hours_requested[0]}</p>
          )}
        </div>
      </div>

      <div>
        <label className={labelClass}>Notes (optional)</label>
        <textarea
          className={inputClass}
          rows={3}
          value={data.reason}
          onChange={(e) => setData("reason", e.target.value)}
          placeholder="Optional notes about these credits..."
        />
        {errors?.reason && <p className={errorClass}>{errors.reason[0]}</p>}
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded-md border bg-white px-4 py-2 text-sm font-medium"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {processing
            ? "Saving..."
            : credit.id
              ? "Update Credits"
              : "Add Credits"}
        </button>
      </div>
    </form>
  )
}
